<script setup lang="ts">
import { reactive, ref, toRaw } from 'vue';

import KBtn from '@/components/btns/KBtn.vue';
import DriverAutocompleteField from '@/components/fields/DriverAutocompleteField.vue';
import StateSelectField from '@/components/fields/StateSelectField.vue';
import DatePickerField from '@/components/pickerFields/DatePickerField.vue';
import useForm from '@/composables/useForm';
import { requiredValidationRule } from '@/helpers/validationRules';

const props = withDefaults(defineProps<Props>(), {
  loading: false,
  initialData: () => ({}),
});
const emit = defineEmits<{
  submit: [formData: ValidData];
}>();

export interface ValidData {
  date: string;
  driver: number | null;
  division: string | null;
}

export interface InitialData {
  date?: string;
  driver?: number;
  division?: string;
}

export interface Props {
  loading?: boolean;
  initialData?: InitialData;
}

const formData = reactive<InitialData>({});
const isValid = ref(true);

const { form, handleSubmit } = useForm(props, formData, () => {
  const raw = toRaw(formData);
  const date = raw.date ?? '';
  emit('submit', {
    date,
    division: raw.division ?? null,
    driver: raw.driver ?? null,
  });
});
</script>

<template>
  <v-form
    ref="form"
    v-model="isValid"
    validate-on="lazy"
    :class="$style.smsDailySearchForm"
    data-testid="smsDailySearchForm"
    @submit.prevent="handleSubmit"
  >
    <DatePickerField
      v-model="formData.date"
      size="lg"
      label="Date"
      :disabled="props.loading"
      :rules="[requiredValidationRule]"
      :class="$style.field"
    />
    <StateSelectField
      v-model="formData.division"
      size="large"
      :disabled="props.loading"
      :class="$style.field"
      @keydown.enter="handleSubmit"
    />
    <DriverAutocompleteField
      v-model="formData.driver"
      size="lg"
      :disabled="props.loading"
      :class="[$style.field, $style.driverField]"
      @keydown.enter="handleSubmit"
    />
    <KBtn
      type="submit"
      size="lg"
      prepend-icon="mdi-magnify"
      :loading="props.loading"
      :class="$style.submit"
    >
      Search
    </KBtn>
  </v-form>
</template>

<!-- prettier-ignore -->
<style module lang="scss">
@use '@/styles/colors';
@use '@/styles/variables';

.smsDailySearchForm {
  display: flex;
  align-items: flex-start;
}
    .field {
      width: 180px;
      display: inline-block;
      vertical-align: top;
      margin: 0 1rem 0.5rem 0;
    }
    .field.driverField {
      width: 320px;
      flex: 0 0 auto;
    }
    .submit {
      margin-top: 0;
    }
    .datePickerField {
      width: 180px;
    }
</style>
